// src/pages/billing/BillingAccountDetailPage.tsx
// B2B2C billing account detail — inline-edit property grid, the only edit
// surface (see docs/ux/14-forms.md). Editable fields mirror what the old
// /edit page covered (name/email/phone/billing address/tax info) — UI shell
// changed, not the editable scope. Currency/billing period stay read-only
// here (the old edit page didn't touch payment_settings either).

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useBillingBillingAccount, useBillingBalance, useI18n } from '@react/hooks';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { FormRow, InlineEditText } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { PaymentMethodsCard } from '@/features/payment/PaymentMethodsCard';
import { CreditGrantsCard } from '@/features/billing/CreditGrantsCard';
import { LegalEntityCard } from '@/features/billing/LegalEntityCard';
import { formatMoney, formatDate, humanizeEnum, errMessage } from '@/features/billing/format';

export const BillingAccountDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useBillingBillingAccount();
  const balanceStore = useBillingBalance();
  const { t } = useI18n();
  const item = store.currentItem as any;
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      store.GetBillingAccount(id);
      balanceStore.ListBalanceTransactions(id).catch(() => {});
    }
    return () => { store.clearCurrent(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const commit = async (fields: Record<string, unknown>) => {
    if (!id) return;
    setSaveError(null);
    try {
      await store.UpdateBillingAccount(id, {
        entity: item?.entity,
        field_mask: [],
        name: item?.name,
        email: item?.email,
        phone: item?.phone,
        billing_address: item?.billing_address,
        tax_info: item?.tax_info,
        ...fields,
      } as any);
    } catch (err) {
      setSaveError(errMessage(err, t('billing.account.saveError')));
    }
  };

  const commitAddress = (field: string, value: string) =>
    commit({ billing_address: { ...(item?.billing_address ?? {}), [field]: value } });

  const commitTax = (field: string, value: string | boolean) =>
    commit({ tax_info: { ...(item?.tax_info ?? {}), [field]: value } });

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error) {
    return <><ErrorAlert error={store.error} /><Link to="/billing/accounts" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></>;
  }
  if (!item) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/billing/accounts" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/billing" className="text-muted-foreground hover:text-foreground">{t('billing.title')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/billing/accounts" className="text-muted-foreground hover:text-foreground">{t('billing.account.listTitle')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.name ?? t('billing.details'))}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <InlineEditText
              value={String(item.name ?? '')}
              onCommit={(next) => commit({ name: next })}
              variant="title"
              placeholder={t('billing.account.namePlaceholder')}
              aria-label={t('common.name')}
              className="max-w-md"
            />
            {item.is_delinquent ? (
              <Badge variant="error" size="sm">{t('billing.account.delinquentWithDays', { days: item.delinquent_days })}</Badge>
            ) : item.is_active ? (
              <Badge variant="success" size="sm">{t('common.active')}</Badge>
            ) : (
              <Badge variant="secondary" size="sm">{t('common.inactive')}</Badge>
            )}
          </div>
          <div className="mt-1">
            <InlineEditText
              value={String(item.email ?? '')}
              onCommit={(next) => commit({ email: next })}
              placeholder={t('billing.account.emailPlaceholder')}
              aria-label={t('billing.account.email')}
              className="max-w-xs text-muted-foreground"
            />
          </div>
        </div>
      </div>

      {saveError && <ErrorAlert error={saveError} dismissible onDismiss={() => setSaveError(null)} />}

      {/* Balance cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('billing.account.currentBalance')}</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.current_balance)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('billing.account.creditLimit')}</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.credit_limit)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('billing.account.availableCredit')}</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.available_credit)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('billing.account.pendingCharges')}</p><p className="text-2xl font-bold text-foreground">{formatMoney(item.pending_charges)}</p></CardContent></Card>
      </div>

      {/* Account info — inline-editable property grid */}
      <Card>
        <CardHeader><CardTitle>{t('billing.account.details')}</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border">
          <FormRow label={t('billing.account.phone')} labelWidth="sm">
            <InlineEditText
              value={String(item.phone ?? '')}
              onCommit={(next) => commit({ phone: next })}
              allowEmpty
              placeholder={t('billing.account.phonePlaceholder')}
              aria-label={t('billing.account.phone')}
            />
          </FormRow>
          <FormRow label={t('billing.account.addressLine1')} labelWidth="sm">
            <InlineEditText
              value={String(item.billing_address?.line1 ?? '')}
              onCommit={(next) => commitAddress('line1', next)}
              allowEmpty
              placeholder={t('common.notSet')}
              aria-label={t('billing.account.addressLine1')}
            />
          </FormRow>
          <FormRow label={t('billing.account.addressLine2')} labelWidth="sm">
            <InlineEditText
              value={String(item.billing_address?.line2 ?? '')}
              onCommit={(next) => commitAddress('line2', next)}
              allowEmpty
              placeholder={t('common.notSet')}
              aria-label={t('billing.account.addressLine2')}
            />
          </FormRow>
          <FormRow label={t('billing.account.city')} labelWidth="sm">
            <InlineEditText
              value={String(item.billing_address?.city ?? '')}
              onCommit={(next) => commitAddress('city', next)}
              allowEmpty
              placeholder={t('common.notSet')}
              aria-label={t('billing.account.city')}
            />
          </FormRow>
          <FormRow label={t('billing.account.state')} labelWidth="sm">
            <InlineEditText
              value={String(item.billing_address?.state ?? '')}
              onCommit={(next) => commitAddress('state', next)}
              allowEmpty
              placeholder={t('common.notSet')}
              aria-label={t('billing.account.state')}
            />
          </FormRow>
          <FormRow label={t('billing.account.postalCode')} labelWidth="sm">
            <InlineEditText
              value={String(item.billing_address?.postal_code ?? '')}
              onCommit={(next) => commitAddress('postal_code', next)}
              allowEmpty
              placeholder={t('common.notSet')}
              aria-label={t('billing.account.postalCode')}
            />
          </FormRow>
          <FormRow label={t('billing.account.country')} labelWidth="sm">
            <InlineEditText
              value={String(item.billing_address?.country ?? '')}
              onCommit={(next) => commitAddress('country', next.toUpperCase())}
              allowEmpty
              placeholder={t('common.notSet')}
              aria-label={t('billing.account.country')}
              className="font-mono"
            />
          </FormRow>
          <FormRow label={t('billing.account.taxId')} labelWidth="sm">
            <InlineEditText
              value={String(item.tax_info?.tax_id ?? '')}
              onCommit={(next) => commitTax('tax_id', next)}
              allowEmpty
              placeholder={t('common.notSet')}
              aria-label={t('billing.account.taxId')}
              className="font-mono"
            />
          </FormRow>
          <FormRow label={t('billing.account.taxIdType')} labelWidth="sm">
            <InlineEditText
              value={String(item.tax_info?.tax_id_type ?? '')}
              onCommit={(next) => commitTax('tax_id_type', next)}
              allowEmpty
              placeholder={t('common.notSet')}
              aria-label={t('billing.account.taxIdType')}
            />
          </FormRow>
          <FormRow label={t('billing.account.taxExempt')} labelWidth="sm">
            <ToggleSwitch
              label={item.tax_info?.tax_exempt ? t('common.yes') : t('common.no')}
              checked={!!item.tax_info?.tax_exempt}
              onChange={(checked) => commitTax('tax_exempt', checked)}
            />
          </FormRow>
          <FormRow label={t('billing.account.currency')} labelWidth="sm">
            <span className="text-sm text-foreground">{humanizeEnum(item.payment_settings?.currency, 'CURRENCY_') || '--'}</span>
          </FormRow>
          <FormRow label={t('billing.account.billingPeriod')} labelWidth="sm">
            <span className="text-sm text-foreground">{humanizeEnum(item.payment_settings?.billing_period, 'BILLING_PERIOD_') || '--'}</span>
          </FormRow>
          <FormRow label={t('common.createdAt')} labelWidth="sm">
            <span className="text-sm text-muted-foreground">{formatDate(item.created_at)}</span>
          </FormRow>
        </CardContent>
      </Card>

      {/* Payment Methods — live management via PaymentService (add/remove/set default/bank requisites) */}
      {id && <PaymentMethodsCard billingAccountId={id} />}

      {/* Legal Entity — links this account to the LegalEntity used for contract bank requisites (AttachLegalEntityToBillingAccount) */}
      {id && (
        <LegalEntityCard
          billingAccountId={id}
          legalEntity={item.legal_entity}
          onAttached={() => store.GetBillingAccount(id)}
        />
      )}

      {/* Credit Grants — grant / void credit on this account (CreateCreditGrant, VoidCreditGrant) */}
      {id && <CreditGrantsCard billingAccountId={id} />}

      {/* Balance Transactions */}
      <Card>
        <CardHeader><CardTitle>{t('billing.account.balanceHistory')}</CardTitle></CardHeader>
        <CardContent>
          {balanceStore.error ? (
            <div className="mb-4"><ErrorAlert error={balanceStore.error} dismissible onDismiss={() => balanceStore.clearError()} /></div>
          ) : null}
          {balanceStore.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : (
            <Table striped hoverable>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.date')}</TableHead>
                  <TableHead>{t('common.type')}</TableHead>
                  <TableHead>{t('common.description')}</TableHead>
                  <TableHead>{t('billing.account.amount')}</TableHead>
                  <TableHead>{t('billing.account.endingBalance')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {balanceStore.items.length === 0 ? (
                  <TableEmpty colSpan={5} message={t('billing.account.noBalanceTransactions')} />
                ) : (
                  balanceStore.items.map((bt: any, i: number) => (
                    <TableRow key={bt.entity?.id || i}>
                      <TableCell>{formatDate(bt.created_at)}</TableCell>
                      <TableCell>{String(bt.type ?? '--')}</TableCell>
                      <TableCell>{String(bt.description ?? '--')}</TableCell>
                      <TableCell>{formatMoney(bt.amount)}</TableCell>
                      <TableCell>{formatMoney(bt.ending_balance)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default BillingAccountDetailPage;
